import type { UseFormSetError } from 'react-hook-form';

import {
  AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
  AGE_EARLY_ELDERLY_NOT_ALLOWED_AT_75_PLUS_CONFIRM,
  AGE_ELDERLY_CERTIFICATE_ERROR,
  LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
  LATE_ELDERLY_INSURER_NUMBER_ERROR,
  LATE_ELDERLY_INSURER_NUMBER_LENGTH_ERROR,
  LATE_ELDERLY_RELATIONSHIP_ERROR,
  MEDICAL_AID_APPLICATION_START_DATE_REQUIRED,
  MEDICAL_AID_PUBLIC_RECIPIENT_NUMBER_REQUIRED,
  OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
  PATIENT_COST_PRESCHOOL_RATE_CHANGE_WARNING,
  RELATIONSHIP_67_INSURER_NUMBER_ERROR,
  HEALTH_INSURANCE_CARD_SYMBOL_REQUIRED,
} from '@pages/main/patient/constants/validationMessages';
import {
  patientFormSchema,
  PatientFormSchemaType,
  trimPatientNameKanaInput,
} from '@pages/main/patient/types';

const PROVISIONAL_SUPPRESSED_MESSAGES = new Set<string>([
  HEALTH_INSURANCE_CARD_SYMBOL_REQUIRED,
  MEDICAL_AID_PUBLIC_RECIPIENT_NUMBER_REQUIRED,
  MEDICAL_AID_APPLICATION_START_DATE_REQUIRED,
  AGE_ELDERLY_CERTIFICATE_ERROR,
  LATE_ELDERLY_INSURER_NUMBER_LENGTH_ERROR,
  LATE_ELDERLY_RELATIONSHIP_ERROR,
  LATE_ELDERLY_INSURER_NUMBER_ERROR,
  RELATIONSHIP_67_INSURER_NUMBER_ERROR,
  PATIENT_COST_PRESCHOOL_RATE_CHANGE_WARNING,
  AGE_70_TO_74_EARLY_ELDERLY_REQUIRED_CONFIRM,
  AGE_EARLY_ELDERLY_NOT_ALLOWED_AT_75_PLUS_CONFIRM,
  OVER_75_MUST_SET_LATE_ELDERLY_CONFIRM,
  LATE_ELDERLY_INSURANCE_UNDER_65_CONFIRM,
]);

const getValueByPath = (obj: unknown, path: ReadonlyArray<string | number>): unknown => {
  return path.reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[String(key)];
  }, obj);
};

const isEmptyValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const validateProvisionalPatientForm = (
  data: PatientFormSchemaType,
  setError: UseFormSetError<PatientFormSchemaType>
): boolean => {
  let hasRequiredErrors = false;
  const name = (data.patient_information?.name ?? '').trim();
  const nameKana = trimPatientNameKanaInput(data.patient_information?.name_kana ?? '');

  if (!name) {
    setError('patient_information.name', {
      type: 'required',
      message: '氏名漢字は必須です。',
    });
    hasRequiredErrors = true;
  }

  if (!nameKana) {
    setError('patient_information.name_kana', {
      type: 'required',
      message: '氏名カナは必須です。',
    });
    hasRequiredErrors = true;
  }

  const sexId = data.patient_information?.sex_id;
  if (sexId !== 1 && sexId !== 2) {
    setError('patient_information.sex_id', {
      type: 'required',
      message: '性別は必須です。',
    });
    hasRequiredErrors = true;
  }

  if (hasRequiredErrors) {
    return false;
  }

  const provisionalValidation = patientFormSchema.safeParse(data);
  if (provisionalValidation.success) {
    return true;
  }

  const filteredIssues = provisionalValidation.error.issues.filter(issue => {
    if (PROVISIONAL_SUPPRESSED_MESSAGES.has(issue.message)) {
      return false;
    }

    const path = issue.path ?? [];
    const pathKey = path.map(String).join('.');
    if (pathKey === 'patient_information.name' || pathKey === 'patient_information.name_kana') {
      return true;
    }

    const issueValue = getValueByPath(data, path);
    return !isEmptyValue(issueValue);
  });

  for (const issue of filteredIssues) {
    const pathKey = issue.path.map(String).join('.');
    if (!pathKey) continue;
    setError(pathKey as any, {
      type: 'manual',
      message: issue.message,
    });
  }

  return filteredIssues.length === 0;
};
